import { useMemo } from 'react';
import { ThemeProvider } from 'styled-components';
import GlobalStyle from '@elementary/common-ui/style/global';
import theme from '@elementary/common-ui/style/theme';
import {
  ThemeProvider as MuiThemeProvider,
  StyledEngineProvider,
} from '@mui/material';
import {
  QueryClient,
  QueryClientProvider,
  MutationCache,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RecoilRoot, useSetRecoilState } from 'recoil';
import 'shared/services/i18n';
import { snackbar } from './shared/store/atoms';

const UNAUTHORIZED_CODE = 'UNAUTHORIZED';
const SIGNATURE_EXPIRED_ERROR_CODE = 8;
const PERMISSION_ERROR_CODE = 54;

const getElementaryErrorCode = (error: any) => {
  const data = error?.response?.data;
  if (data === undefined) {
    // in case data is not provided (eg. the graphql-request library doesn't provide access to the error response data),
    // we assume that the error is a signature expired error
    return SIGNATURE_EXPIRED_ERROR_CODE;
  }
  return data?.elementary_error_code;
};

const formatPermissionErrorMessage = (
  objectType?: string,
  objectIds?: string[],
): string => {
  if (!objectType) {
    return 'Change not saved. Edit access required.';
  }

  const lowercaseType = objectType.toLowerCase();
  const isPlural = Array.isArray(objectIds) && objectIds.length > 1;
  const displayType = isPlural ? `${lowercaseType}s` : lowercaseType;

  return `Changes not saved. You don't have permission to edit the selected ${displayType}.`;
};

const getPermissionErrorMessage = (error: any): string | null => {
  const elementaryErrorCode = getElementaryErrorCode(error);

  if (elementaryErrorCode === PERMISSION_ERROR_CODE) {
    const extra = error?.response?.data?.extra;
    return formatPermissionErrorMessage(extra?.object_type, extra?.object_ids);
  }

  const graphqlErrors = error?.response?.errors;
  if (Array.isArray(graphqlErrors)) {
    for (const gqlError of graphqlErrors) {
      if (gqlError?.extensions?.code === UNAUTHORIZED_CODE) {
        return formatPermissionErrorMessage(
          gqlError?.extensions?.object_type,
          gqlError?.extensions?.object_ids,
        );
      }
    }
  }

  return null;
};

const QueryClientProviderWithPermissionHandling = ({
  children,
  disableCaching,
}: {
  children: React.ReactNode;
  disableCaching?: boolean;
}) => {
  const setSnackbar = useSetRecoilState(snackbar);

  const queryClient = useMemo(
    () =>
      new QueryClient({
        mutationCache: new MutationCache({
          onError: (error) => {
            const errorMessage = getPermissionErrorMessage(error);
            if (errorMessage !== null) {
              setSnackbar((prev) => ({
                ...prev,
                open: true,
                variant: 'error',
                message: errorMessage,
                autoHide: false,
              }));
            }
            // Component-level onError handlers will still be called after this
          },
        }),
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            retry: (failureCount, error) => {
              const elementaryErrorCode = getElementaryErrorCode(error);
              const isSignatureExpired =
                elementaryErrorCode === SIGNATURE_EXPIRED_ERROR_CODE;
              return (
                failureCount <= 3 &&
                (error as any).response?.status === 400 &&
                isSignatureExpired
              );
            },
            retryDelay: 3000,
            staleTime: disableCaching ? 0 : Infinity,
          },
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disableCaching],
  );

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <ReactQueryDevtools initialIsOpen={false} position="bottom-left" />
    </QueryClientProvider>
  );
};

const AppProviders = ({
  children,
  disableCaching,
}: {
  children: React.ReactNode;
  disableCaching?: boolean;
}) => (
  <RecoilRoot>
    <QueryClientProviderWithPermissionHandling disableCaching={disableCaching}>
      <StyledEngineProvider injectFirst>
        <MuiThemeProvider theme={theme}>
          <ThemeProvider theme={theme}>
            <GlobalStyle />
            {children}
          </ThemeProvider>
        </MuiThemeProvider>
      </StyledEngineProvider>
    </QueryClientProviderWithPermissionHandling>
  </RecoilRoot>
);

export default AppProviders;
